/**
 * Global Meetings Hook - Cross-project meeting aggregation
 * Light QA Contract Compliant:
 * - No SELECT * - only required fields
 * - Pagination with .limit(100)
 * - RLS automatic filtering by user permissions
 * - placeholderData: keepPreviousData to prevent flicker
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import type { MeetingStatus } from '@/types/meeting';
import type { ProjectMark } from '@/constants/projectMarks';
import { chunkedInQuerySimple } from '@/lib/queries/chunkedInQuery';

export type CompanyFilter = 'Solid' | 'ENP' | 'unassigned';
export type ProjectStatusFilter = 'intake' | 'tender' | 'build' | 'defects' | 'service' | 'inactive';

export interface GlobalMeetingFilters {
  status?: MeetingStatus;
  projectIds?: string[];  // Changed from projectId to projectIds (array)
  searchQuery?: string;
  dateRange?: {
    start?: string;
    end?: string;
  };
  projectMark?: ProjectMark;
  memberIds?: string[];   // Changed from memberId to memberIds (array)
  company?: CompanyFilter;
  projectStatus?: ProjectStatusFilter;
  onlyFavorites?: boolean; // Filter to show only favorited meetings
}

export interface GlobalMeeting {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  meeting_date: string;
  start_time: string | null;
  end_time: string | null;
  location: string | null;
  status: MeetingStatus;
  created_at: string;
  project_name: string;
  company: string | null;
  project_image: string | null;
  project_mark: ProjectMark | null;
  project_status: string | null; // Project status for table view
  can_edit: boolean; // Permission flag - default true, server-side RLS will reject if no permission
}

// Query keys for cache management
export const globalMeetingKeys = {
  all: ['global-meetings'] as const,
  list: (filters?: GlobalMeetingFilters) => [...globalMeetingKeys.all, 'list', filters] as const,
  stats: () => [...globalMeetingKeys.all, 'stats'] as const,
  members: () => [...globalMeetingKeys.all, 'members'] as const,
};

/**
 * Hook to fetch all meetings across all projects user has access to
 * RLS policies automatically filter based on project_user_permissions
 */
export const useGlobalMeetings = (filters?: GlobalMeetingFilters) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: globalMeetingKeys.list(filters),
    queryFn: async () => {
      // Select only required fields (no SELECT *)
      let query = supabase
        .from('meetings')
        .select(`
          id,
          project_id,
          title,
          description,
          meeting_date,
          start_time,
          end_time,
          location,
          status,
          created_at,
          projects!inner(project_name, company, image_url, project_mark, status)
        `)
        .order('meeting_date', { ascending: false })
        .order('start_time', { ascending: false })
        .limit(100); // Pagination - Light QA Contract

      // Apply filters
      if (filters?.status) {
        query = query.eq('status', filters.status);
      }

      // Filter by project IDs (array) - typically comes from user filter, could exceed 100
      if (filters?.projectIds && filters.projectIds.length > 0) {
        // Note: projectIds filter is user-controlled, but typically small (<50 projects selected)
        // If this becomes an issue, we need to restructure to fetch meetings per project chunk
        // eslint-disable-next-line no-restricted-syntax
        query = query.in('project_id', filters.projectIds);
      }

      if (filters?.searchQuery) {
        query = query.or(`title.ilike.%${filters.searchQuery}%,description.ilike.%${filters.searchQuery}%`);
      }

      if (filters?.dateRange?.start) {
        query = query.gte('meeting_date', filters.dateRange.start);
      }

      if (filters?.dateRange?.end) {
        query = query.lte('meeting_date', filters.dateRange.end);
      }

      // Filter by project mark
      if (filters?.projectMark) {
        query = query.eq('projects.project_mark', filters.projectMark);
      }

      // Filter by company
      if (filters?.company) {
        if (filters.company === 'unassigned') {
          query = query.is('projects.company', null);
        } else {
          query = query.eq('projects.company', filters.company);
        }
      }

      // Filter by project status
      if (filters?.projectStatus) {
        query = query.eq('projects.status', filters.projectStatus);
      }

      // Filter by member IDs (array) - get meetings where user is an attendee
      if (filters?.memberIds && filters.memberIds.length > 0) {
        // Use chunkedInQuerySimple for member IDs which can exceed 100
        const memberMeetings = await chunkedInQuerySimple<{ meeting_id: string }>(
          supabase,
          'meeting_attendees',
          'user_id',
          filters.memberIds,
          'meeting_id'
        );
        
        if (memberMeetings.length > 0) {
          const ids = [...new Set(memberMeetings.map(m => m.meeting_id))];
          // eslint-disable-next-line no-restricted-syntax
          query = query.in('id', ids);
        } else {
          // No meetings found for these members
          return [];
        }
      }

      // Filter by favorites - get meetings user has favorited
      if (filters?.onlyFavorites && user?.id) {
        const { data: favoriteIds } = await supabase
          .from('user_meeting_favorites')
          .select('meeting_id')
          .eq('user_id', user.id);
        
        if (favoriteIds && favoriteIds.length > 0) {
          const ids = favoriteIds.map(f => f.meeting_id);
          // Note: User favorites are typically small (<100), but adding eslint-disable for safety
          // eslint-disable-next-line no-restricted-syntax
          query = query.in('id', ids);
        } else {
          // No favorites found
          return [];
        }
      }

      const { data, error } = await query;

      if (error) {
        if (import.meta.env.DEV) console.error('[useGlobalMeetings] Error:', error.message);
        throw error;
      }

      // Type for Supabase query result with joined projects
      interface MeetingQueryResult {
        id: string;
        project_id: string;
        title: string;
        description: string | null;
        meeting_date: string;
        start_time: string | null;
        end_time: string | null;
        location: string | null;
        status: MeetingStatus;
        created_at: string;
        projects: {
          project_name: string;
          company: string | null;
          image_url: string | null;
          project_mark: ProjectMark | null;
          status: string | null;
        } | null;
      }

      // Fix 3: Query actual can_edit permissions from project_user_permissions table
      const typedData = (data || []) as MeetingQueryResult[];
      const projectIds = [...new Set(typedData.map((m) => m.project_id))];
      
      let permissionMap = new Map<string, boolean>();
      
      if (projectIds.length > 0 && user?.id) {
        // Use chunkedInQuerySimple for project IDs which can exceed 100
        const permissions = await chunkedInQuerySimple<{ project_id: string; can_edit: boolean }>(
          supabase,
          'project_user_permissions',
          'project_id',
          projectIds,
          'project_id, can_edit',
          (query) => query.eq('user_id', user.id)
        );
        
        if (permissions.length > 0) {
          permissionMap = new Map(
            permissions.map(p => [p.project_id, p.can_edit ?? false])
          );
        }
        if (import.meta.env.DEV) console.log('[useGlobalMeetings] Permission map:', Object.fromEntries(permissionMap));
      }

      // Transform data to flatten project info
      const meetings: GlobalMeeting[] = typedData.map((meeting) => ({
        id: meeting.id,
        project_id: meeting.project_id,
        title: meeting.title,
        description: meeting.description,
        meeting_date: meeting.meeting_date,
        start_time: meeting.start_time,
        end_time: meeting.end_time,
        location: meeting.location,
        status: meeting.status,
        created_at: meeting.created_at,
        project_name: meeting.projects?.project_name || 'Unknown',
        company: meeting.projects?.company || null,
        project_image: meeting.projects?.image_url || null,
        project_mark: meeting.projects?.project_mark || null,
        project_status: meeting.projects?.status || null,
        can_edit: permissionMap.get(meeting.project_id) ?? false, // Query from DB, default false if no permission
      }));

      return meetings;
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    placeholderData: keepPreviousData, // Prevent flicker on refetch
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch meeting stats across all projects
 */
export const useGlobalMeetingStats = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: globalMeetingKeys.stats(),
    queryFn: async () => {
      // Get counts by status
      const { data, error } = await supabase
        .from('meetings')
        .select('status', { count: 'exact' });

      if (error) {
        if (import.meta.env.DEV) console.error('[useGlobalMeetingStats] Error:', error.message);
        throw error;
      }

      // Calculate stats
      const stats = {
        total: data?.length || 0,
        upcoming: 0,
        in_progress: 0,
        completed: 0,
        cancelled: 0,
      };

      // Type for stats query result
      interface StatsQueryResult {
        status: MeetingStatus;
      }

      (data as StatsQueryResult[] | null)?.forEach((meeting) => {
        const status = meeting.status;
        if (status in stats) {
          stats[status as keyof typeof stats]++;
        }
      });

      return stats;
    },
    enabled: !!user?.id,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

/**
 * Hook to fetch unique projects from meetings for filter dropdown
 */
export const useGlobalMeetingProjects = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: [...globalMeetingKeys.all, 'projects'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('meetings')
        .select('project_id, projects!inner(project_name)')
        .limit(100);

      if (error) {
        if (import.meta.env.DEV) console.error('[useGlobalMeetingProjects] Error:', error.message);
        throw error;
      }

      // Type for projects query result
      interface ProjectsQueryResult {
        project_id: string;
        projects: { project_name: string } | null;
      }

      // Get unique projects
      const projectMap = new Map<string, string>();
      (data as ProjectsQueryResult[] | null)?.forEach((meeting) => {
        if (!projectMap.has(meeting.project_id)) {
          projectMap.set(meeting.project_id, meeting.projects?.project_name || 'Unknown');
        }
      });

      return Array.from(projectMap.entries()).map(([id, name]) => ({
        id,
        name,
      }));
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

/**
 * Hook to fetch unique members (attendees) from meetings for filter dropdown
 * JOINs with profiles table to get actual user names
 */
export const useGlobalMeetingMembers = () => {
  const { user } = useAuth();

  return useQuery({
    queryKey: globalMeetingKeys.members(),
    queryFn: async () => {
      // Step 1: Get unique user_ids from meeting_attendees
      const { data: attendees, error: attendeesError } = await supabase
        .from('meeting_attendees')
        .select('user_id')
        .not('user_id', 'is', null)
        .limit(200);

      if (attendeesError) {
        if (import.meta.env.DEV) console.error('[useGlobalMeetingMembers] Error:', attendeesError.message);
        throw attendeesError;
      }

      // Get unique user IDs
      const uniqueUserIds = [...new Set(attendees?.map(a => a.user_id).filter(Boolean))] as string[];
      
      if (uniqueUserIds.length === 0) {
        return [];
      }

      // Step 2: Fetch profiles for these users (including avatar_url)
      const profiles = await chunkedInQuerySimple<{ id: string; full_name: string; email: string; avatar_url: string | null }>(
        supabase,
        'profiles',
        'id',
        uniqueUserIds,
        'id, full_name, email, avatar_url'
      );

      // Transform to member format
      return (profiles || []).map(profile => ({
        id: profile.id,
        name: profile.full_name || 'Unknown',
        email: profile.email || null,
        avatar_url: profile.avatar_url || null,
      }));
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

/**
 * Hook to update a meeting's date (for drag-and-drop reschedule)
 * Implements optimistic update for instant UI feedback with rollback on failure
 */
export const useUpdateGlobalMeeting = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      meetingId, 
      updates 
    }: { meetingId: string; updates: { meeting_date: string } }) => {
      if (import.meta.env.DEV) console.log('[useUpdateGlobalMeeting] Starting mutation:', { meetingId, updates });
      
      const { data, error } = await supabase
        .from('meetings')
        .update({
          meeting_date: updates.meeting_date,
          updated_at: new Date().toISOString(),
        })
        .eq('id', meetingId)
        .select()
        .maybeSingle();
      
      if (import.meta.env.DEV) console.log('[useUpdateGlobalMeeting] Response:', { data, error });
      
      if (error) {
        if (import.meta.env.DEV) console.error('[useUpdateGlobalMeeting] Supabase error:', error);
        throw error;
      }
      
      if (!data) {
        if (import.meta.env.DEV) console.error('[useUpdateGlobalMeeting] No data returned - permission denied or meeting not found');
        throw new Error('Không thể cập nhật cuộc họp. Bạn có thể không có quyền hoặc cuộc họp không tồn tại.');
      }
      
      if (import.meta.env.DEV) console.log('[useUpdateGlobalMeeting] Success:', data);
      return data;
    },

    // ✅ OPTIMISTIC UPDATE - Cập nhật UI ngay lập tức trước khi server response
    onMutate: async ({ meetingId, updates }) => {
      if (import.meta.env.DEV) console.log('[useUpdateGlobalMeeting] onMutate - optimistic update:', { meetingId, updates });
      
      // Predicate để chỉ target LIST queries (có structure GlobalMeeting[])
      // Tránh match stats/members/projects queries vì chúng không phải array
      const listQueryPredicate = (query: { queryKey: readonly unknown[] }) => 
        query.queryKey[0] === 'global-meetings' && query.queryKey[1] === 'list';
      
      // Cancel any outgoing refetches chỉ cho list queries
      await queryClient.cancelQueries({ predicate: listQueryPredicate });
      
      // Snapshot tất cả list queries để rollback nếu cần
      const previousData = new Map<readonly unknown[], unknown>();
      
      // Get chỉ list queries với predicate
      const queries = queryClient.getQueriesData<GlobalMeeting[]>({ 
        predicate: listQueryPredicate 
      });
      if (import.meta.env.DEV) console.log('[useUpdateGlobalMeeting] Found list queries to update:', queries.length);
      
      queries.forEach(([queryKey, data]) => {
        if (data && Array.isArray(data)) {
          previousData.set(queryKey, data);
          if (import.meta.env.DEV) console.log('[useUpdateGlobalMeeting] Saved snapshot for:', queryKey);
        }
      });
      
      // Optimistically update chỉ list queries với predicate + type guard
      queryClient.setQueriesData<GlobalMeeting[]>(
        { predicate: listQueryPredicate },
        (old) => {
          // Type guard: đảm bảo old là array trước khi .map()
          if (!old || !Array.isArray(old)) return old;
          const updated = old.map(meeting => 
            meeting.id === meetingId 
              ? { ...meeting, meeting_date: updates.meeting_date }
              : meeting
          );
          if (import.meta.env.DEV) console.log('[useUpdateGlobalMeeting] Updated cache, meeting moved to:', updates.meeting_date);
          return updated;
        }
      );
      
      // Return context với previous data để rollback
      return { previousData };
    },

    // ✅ ROLLBACK nếu mutation fail
    onError: (error: Error, variables, context) => {
      if (import.meta.env.DEV) console.error('[useUpdateGlobalMeeting] onError - rolling back:', error.message);
      
      // Rollback tất cả queries về previous state
      if (context?.previousData) {
        context.previousData.forEach((data, queryKey) => {
          queryClient.setQueryData(queryKey, data);
        });
        if (import.meta.env.DEV) console.log('[useUpdateGlobalMeeting] Rollback completed');
      }
      
      // Enhanced error toast với duration dài hơn và icon rõ ràng
      toast.error(
        error.message || 'Không thể cập nhật cuộc họp',
        {
          duration: 8000, // 8 giây để user đọc kịp
          description: 'Cuộc họp đã được trả về vị trí ban đầu. Bạn có thể không có quyền chỉnh sửa cuộc họp này.',
        }
      );
    },

    onSuccess: (data) => {
      if (import.meta.env.DEV) console.log('[useUpdateGlobalMeeting] onSuccess - data synced with server');
      // Không cần invalidate ngay vì onSettled sẽ lo
    },

    // ✅ ALWAYS sync với server sau khi mutation hoàn tất (success hoặc error)
    onSettled: () => {
      if (import.meta.env.DEV) console.log('[useUpdateGlobalMeeting] onSettled - invalidating queries');
      queryClient.invalidateQueries({ queryKey: globalMeetingKeys.all });
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
    }
  });
};
